/**
 * Telnyx web calling for the dashboard softphone (the browser half of
 * server/lib/telnyxAppRing + server/lib/telnyxBrowserOutbound).
 *
 *   GET  /api/telnyx/app-ring/token     → login JWT for this user's credential, heartbeat period,
 *                                         and `outbound: true` when TELNYX_BROWSER_OUTBOUND is on
 *   POST /api/telnyx/app-ring/presence  heartbeat while connected; {online:false} on the way out
 *   GET  /api/telnyx/app-ring/incoming?via=  who is really calling (the INVITE shows OUR number)
 *   POST /api/telnyx/app-ring/answered  stamp the call row when this browser answers
 *   POST /api/telnyx/browser-outbound/prepare  single-use SIP ticket for an outbound call
 *
 * The SDK is imported lazily: an org with no Telnyx number gets
 * `enabled:false` from the token route and never downloads it.
 */
import type { EndReason } from './callSlots';
import { CallEmitter, type VoiceCall, type VoiceCallEvents } from './voiceCall';

type ApiRequest = (method: string, url: string, data?: unknown) => Promise<any>;

export interface TelnyxTokenResponse {
  enabled: boolean;
  reason?: string;
  loginToken?: string;
  sipUsername?: string;
  expiresAt?: string;
  heartbeatSeconds?: number;
  outbound?: boolean;
}

export interface TelnyxOffer {
  callSid: string;
  from: string;
  to: string;
  callerName?: string | null;
  callControl?: boolean;
}

/** Telnyx call states that mean the call is over. */
const TERMINAL = new Set(['hangup', 'destroy', 'purge']);

/**
 * Map one Telnyx call-state change to the normalised event it means, if any.
 * Pure for tests.
 */
export function telnyxStateEvent(state: string, direction: string): 'ringing' | 'accepted' | 'ended' | null {
  if (TERMINAL.has(state)) return 'ended';
  if (state === 'active') return 'accepted';
  if (direction === 'outbound' && (state === 'ringing' || state === 'early')) return 'ringing';
  return null;
}

/** Why a Telnyx call ended, from what we know locally. Pure for tests. */
export function telnyxEndReason(a: { localEnd: EndReason | null; wasActive: boolean; direction: string; cause?: string }): EndReason {
  if (a.localEnd) return a.localEnd;
  if (a.wasActive) return 'remote';
  if (a.direction === 'inbound') return 'canceled';
  return a.cause && /REJECT|BUSY|DECLINE/i.test(a.cause) ? 'rejected' : 'remote';
}

/** Telnyx SDK Call → VoiceCall. */
export class TelnyxVoiceCall implements VoiceCall {
  readonly provider = 'telnyx' as const;
  private em = new CallEmitter();
  private localEnd: EndReason | null = null;
  private wasActive = false;
  private muted = false;
  sid: string | undefined;

  constructor(readonly raw: any) {}

  /** Fed every callUpdate for this call by TelnyxVoiceClient. */
  handleState(state: string) {
    const ev = telnyxStateEvent(state, this.raw?.direction);
    if (ev === 'accepted') { this.wasActive = true; this.attachAudio(); this.em.emit('accepted'); }
    else if (ev === 'ringing') this.em.emit('ringing');
    else if (ev === 'ended') {
      this.detachAudio();
      this.em.emit('ended', telnyxEndReason({
        localEnd: this.localEnd, wasActive: this.wasActive, direction: this.raw?.direction, cause: this.raw?.cause,
      }));
    }
  }

  // One <audio> per call: with call waiting two calls can be up at once, and a
  // single shared element would play only whichever attached last.
  private audioEl: HTMLAudioElement | null = null;
  private attachAudio() {
    const stream = this.raw?.remoteStream;
    if (!stream || typeof document === 'undefined') return;
    if (!this.audioEl) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.style.display = 'none';
      document.body.appendChild(el);
      this.audioEl = el;
    }
    if (this.audioEl.srcObject !== stream) this.audioEl.srcObject = stream;
    void this.audioEl.play?.().catch(() => {});
  }
  private detachAudio() {
    if (this.audioEl) { this.audioEl.srcObject = null; this.audioEl.remove(); }
    this.audioEl = null;
  }

  get isEnded() { return this.em.ended; }
  callSid() { return this.sid; }
  accept() { this.raw.answer({ video: false }); }
  dismiss() {
    this.localEnd = 'canceled';
    try { this.raw.hangup(); } catch { /* already gone */ }
    this.detachAudio();
    this.em.emit('ended', 'canceled');
  }
  isGone() { return this.em.ended || TERMINAL.has(String(this.raw?.state || '')); }
  reject() {
    this.localEnd = 'rejected';
    try { this.raw.hangup(); } catch (e) { console.warn('[TELNYX] reject failed:', e); }
    this.detachAudio();
    this.em.emit('ended', 'rejected');
  }
  hangup() {
    this.localEnd = 'hangup';
    try { this.raw.hangup(); } catch (e) { console.warn('[TELNYX] hangup failed:', e); }
    this.detachAudio();
    this.em.emit('ended', 'hangup');
  }
  mute(m: boolean) {
    if (m) this.raw.muteAudio(); else this.raw.unmuteAudio();
    this.muted = m;
  }
  isMuted() { return typeof this.raw?.isAudioMuted === 'boolean' ? this.raw.isAudioMuted : this.muted; }
  sendDigits(d: string) { this.raw.dtmf(d); }
  async localHold(on: boolean) { if (on) await this.raw.hold(); else await this.raw.unhold(); }
  on<E extends keyof VoiceCallEvents>(e: E, cb: VoiceCallEvents[E]) { this.em.on(e, cb); }
}

export interface TelnyxClientHandlers {
  onIncoming: (call: TelnyxVoiceCall, offer: TelnyxOffer | null) => void;
  onStatus?: (status: 'ready' | 'offline') => void;
}

export class TelnyxVoiceClient {
  private client: any = null;
  private calls = new Map<string, TelnyxVoiceCall>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private renew: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  outboundEnabled = false;
  ready = false;

  constructor(private api: ApiRequest, private handlers: TelnyxClientHandlers) {}

  /** Log in if the server hands this user a Telnyx credential; resolves false when it does not. */
  async start(): Promise<boolean> {
    this.stopped = false;
    const tok: TelnyxTokenResponse = await this.api('GET', '/api/telnyx/app-ring/token').catch((e: any) => {
      console.warn('[TELNYX] token unavailable:', e?.message || e);
      return { enabled: false };
    });
    if (!tok?.enabled || !tok.loginToken || this.stopped) return false;
    this.outboundEnabled = tok.outbound === true;

    const { TelnyxRTC } = await import('@telnyx/webrtc');
    if (this.stopped) return false;
    // ringtoneFile: the SDK loops it while an inbound call rings (the Twilio
    // SDK rings on its own; without this a Telnyx call would ring silently).
    const client = new TelnyxRTC({ login_token: tok.loginToken, ringtoneFile: '/notification-sound.mp3' });
    this.client = client;

    client.on('telnyx.ready', () => {
      this.ready = true;
      this.startHeartbeat(tok.heartbeatSeconds || 60);
      this.handlers.onStatus?.('ready');
    });
    const offline = () => {
      this.ready = false;
      this.stopHeartbeat();
      this.handlers.onStatus?.('offline');
    };
    client.on('telnyx.socket.close', offline);
    client.on('telnyx.error', (e: any) => { console.error('[TELNYX] client error:', e); offline(); });
    client.on('telnyx.notification', (n: any) => {
      if (n?.type === 'callUpdate' && n.call) this.onCallUpdate(n.call);
    });
    client.connect();

    // The login JWT lasts ~23h; log in again with a fresh one before it lapses.
    const expires = tok.expiresAt ? Date.parse(tok.expiresAt) : NaN;
    if (Number.isFinite(expires)) {
      const ms = Math.max(60_000, expires - Date.now() - 10 * 60_000);
      this.renew = setTimeout(() => { void this.restart(); }, Math.min(ms, 2 ** 31 - 1));
    }
    return true;
  }

  private async restart() {
    // Never drop a live call to renew the token; try again in a minute.
    if (this.calls.size > 0) {
      this.renew = setTimeout(() => { void this.restart(); }, 60_000);
      return;
    }
    this.teardown(false);
    await this.start().catch((e) => console.error('[TELNYX] re-login failed:', e));
  }

  private startHeartbeat(seconds: number) {
    this.stopHeartbeat();
    const beat = () => this.api('POST', '/api/telnyx/app-ring/presence', { online: true })
      .catch((e: any) => console.warn('[TELNYX] heartbeat failed:', e?.message || e));
    void beat();
    this.heartbeat = setInterval(beat, Math.max(15, seconds) * 1000);
  }

  private stopHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private onCallUpdate(raw: any) {
    const id = String(raw.id);
    let call = this.calls.get(id);
    if (!call) {
      if (TERMINAL.has(raw.state)) return;
      call = new TelnyxVoiceCall(raw);
      this.calls.set(id, call);
      call.on('ended', () => this.calls.delete(id));
      if (raw.direction === 'inbound') {
        const c = call;
        // The INVITE's caller number is the org number that was dialled; ask
        // the server which call this is (caller, CallSid, conference or not).
        const via = raw.options?.remoteCallerNumber || raw.options?.callerNumber || '';
        this.api('GET', `/api/telnyx/app-ring/incoming?via=${encodeURIComponent(via)}`)
          .then((r: any) => r?.call as TelnyxOffer | null)
          .catch(() => null)
          .then((offer) => {
            if (offer?.callSid) c.sid = offer.callSid;
            if (!c.isEnded) this.handlers.onIncoming(c, offer);
          });
      }
    }
    call.handleState(String(raw.state));
  }

  /** Report that this browser answered a ringing offer (stamps the call row). */
  reportAnswered(callSid: string) {
    return this.api('POST', '/api/telnyx/app-ring/answered', { callSid })
      .catch((e: any) => console.warn('[TELNYX] answered report failed:', e?.message || e));
  }

  /**
   * Ask the server how to place an outbound call from `callerId`. Returns null
   * when it is not a Telnyx caller ID (use Twilio). Throws the server's message
   * when a gate refuses it — never falls back to Twilio for a Telnyx number.
   */
  async prepareOutbound(to: string, callerId: string): Promise<{ sipUri: string } | null> {
    if (!this.outboundEnabled) return null;
    let r: any;
    try {
      r = await this.api('POST', '/api/telnyx/browser-outbound/prepare', { to, callerId });
    } catch (e: any) {
      if (e?.data?.provider === 'telnyx') throw new Error(e.data.message || "Couldn't place the call.");
      throw e;
    }
    if (!r || r.provider !== 'telnyx') return null;
    if (r.error || !r.sipUri) throw new Error(r.message || "Couldn't place the call.");
    return { sipUri: r.sipUri };
  }

  /** Dial a prepared ticket URI. */
  connect(sipUri: string, callerId: string): TelnyxVoiceCall {
    if (!this.client || !this.ready) throw new Error('Telnyx calling is not connected yet. Please try again.');
    const raw = this.client.newCall({ destinationNumber: sipUri, callerNumber: callerId, audio: true, video: false });
    const call = new TelnyxVoiceCall(raw);
    this.calls.set(String(raw.id), call);
    call.on('ended', () => this.calls.delete(String(raw.id)));
    return call;
  }

  private teardown(goodbye: boolean) {
    this.ready = false;
    this.stopHeartbeat();
    if (this.renew) clearTimeout(this.renew);
    this.renew = null;
    if (goodbye) this.sayOffline();
    try { this.client?.disconnect?.(); } catch { /* already closed */ }
    this.client = null;
  }

  /** Best-effort offline mark that survives page unload. */
  sayOffline() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      void fetch('/api/telnyx/app-ring/presence', {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ online: false }),
      }).catch(() => {});
    } catch { /* storage blocked */ }
  }

  stop() {
    this.stopped = true;
    this.calls.forEach((c) => { if (!c.isEnded) c.hangup(); });
    this.calls.clear();
    this.teardown(true);
  }
}
